/**
 * The Direct Sale Fonts: the five family + weight pairs an end customer can
 * set text in. The web text panel offers exactly these, the commit refuses
 * any other face, and the API renders the print file from a bundled static
 * TrueType instance per entry. A face enters this list only together with
 * its @fontsource import in the web and its TTF in the API.
 */

export interface DirectSaleFontFace {
  fontFamily: string;
  /** CSS numeric weight of the one instance that is offered. */
  fontWeight: number;
}

export const DIRECT_SALE_FONT_FACES: readonly [
  DirectSaleFontFace,
  ...DirectSaleFontFace[],
] = [
  { fontFamily: 'Archivo Black', fontWeight: 400 },
  { fontFamily: 'Inter', fontWeight: 800 },
  { fontFamily: 'Oswald', fontWeight: 500 },
  { fontFamily: 'Playfair Display', fontWeight: 700 },
  { fontFamily: 'Permanent Marker', fontWeight: 400 },
];

/**
 * A CSS font weight as a number: `normal` is 400, `bold` 700, numbers and
 * numeric strings pass. Anything else (`bolder`, garbage) is null, because
 * its meaning depends on the parent and a print file cannot guess it.
 */
export function normalizeFontWeight(value: unknown): number | null {
  if (value === undefined || value === null || value === 'normal') return 400;
  if (value === 'bold') return 700;
  const weight =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && /^\d{3}$/.test(value.trim())
        ? Number(value.trim())
        : NaN;
  return Number.isInteger(weight) && weight >= 100 && weight <= 900
    ? weight
    : null;
}

/** The offered face for a family and weight, or null when it is not one. */
export function findDirectSaleFontFace(
  fontFamily: unknown,
  fontWeight: unknown
): DirectSaleFontFace | null {
  if (typeof fontFamily !== 'string') return null;
  const weight = normalizeFontWeight(fontWeight);
  return (
    DIRECT_SALE_FONT_FACES.find(
      (face) => face.fontFamily === fontFamily && face.fontWeight === weight
    ) ?? null
  );
}
